import type { EpisodeLine } from "./stage-api";

/** Optional editorial media, bound to the exact spoken turn. Times are line-relative. */
export interface EditionVisualCue {
  id: string;
  lineText: string;
  speaker: string;
  atMs: number;
  untilMs: number;
  layout: "inset" | "cutaway";
  kind: "image" | "video";
  url: string;
  sha256: string;
  posterUrl?: string;
  posterSha256?: string;
  clipStartMs?: number;
  alt: string;
  credit: string;
  sourceUrl: string;
  /** e.g. Dock Street, Brooklyn · October 2026, or Archive footage · 2025. */
  context: string;
  /** Retained for editorial review; never presented as proof of permission. */
  reuseBasis: string;
}

const hashPattern = /^[a-f0-9]{64}$/;
const imageExtensions = ["jpg", "png", "webp"];
export function visualAssetUrl(url: unknown, hash: unknown, video = false): url is string {
  return typeof url === "string" && typeof hash === "string" && hashPattern.test(hash) &&
    (video ? ["mp4"] : imageExtensions).some(ext => url === `/api/edition-media/${hash}.${ext}`);
}
function text(value: unknown, max: number): value is string {
  return typeof value === "string" && value.trim().length > 0 && value.length <= max && !/[\x00-\x1f\x7f]/.test(value);
}
export function visualSourceUrl(value: unknown): value is string {
  if (!text(value, 2000)) return false;
  try { const u = new URL(value); return u.protocol === "https:" && !u.username && !u.password; }
  catch { return false; }
}

/** Fail closed per turn: stale, overlapping or malformed cues never fetch media. */
export function validatedVisuals(line: EpisodeLine): EditionVisualCue[] {
  if (!Array.isArray(line.visuals) || line.visuals.length > 8) return [];
  const ids = new Set<string>();
  let previousEnd = 0;
  for (const cue of line.visuals) {
    if (!cue || !text(cue.id, 80) || ids.has(cue.id) || cue.lineText !== line.text || cue.speaker !== line.speaker ||
      !Number.isSafeInteger(cue.atMs) || !Number.isSafeInteger(cue.untilMs) ||
      cue.atMs < previousEnd || cue.untilMs <= cue.atMs || cue.untilMs > line.durationMs ||
      !["inset", "cutaway"].includes(cue.layout) || !["image", "video"].includes(cue.kind) ||
      !visualAssetUrl(cue.url, cue.sha256, cue.kind === "video") ||
      !text(cue.alt, 500) || !text(cue.credit, 200) || !text(cue.context, 200) ||
      !text(cue.reuseBasis, 2000) || !visualSourceUrl(cue.sourceUrl)) return [];
    if (cue.kind === "video") {
      if (!visualAssetUrl(cue.posterUrl, cue.posterSha256) || !Number.isSafeInteger(cue.clipStartMs ?? 0) ||
        (cue.clipStartMs ?? 0) < 0 || (cue.clipStartMs ?? 0) > 3_600_000) return [];
    } else if (cue.posterUrl !== undefined || cue.posterSha256 !== undefined || cue.clipStartMs !== undefined) return [];
    ids.add(cue.id); previousEnd = cue.untilMs;
  }
  return line.visuals;
}

export function visualAt(cues: readonly EditionVisualCue[], elapsedMs: number): EditionVisualCue | null {
  if (!Number.isFinite(elapsedMs) || elapsedMs < 0) return null;
  return cues.find(cue => elapsedMs >= cue.atMs && elapsedMs < cue.untilMs) ?? null;
}

export function visualVideoSeconds(cue: EditionVisualCue, elapsedMs: number): number {
  return ((cue.clipStartMs ?? 0) + Math.max(0, Math.min(cue.untilMs - cue.atMs, elapsedMs - cue.atMs))) / 1000;
}

/** Fade within the cue's bounds using the speech clock, including late-loaded media. */
export function visualOpacity(cue: EditionVisualCue, elapsedMs: number, readyAtMs: number, motionEnabled: boolean): number {
  if (!Number.isFinite(elapsedMs) || !Number.isFinite(readyAtMs) || elapsedMs < cue.atMs || elapsedMs >= cue.untilMs) return 0;
  if (!motionEnabled) return 1;
  const start = Math.max(cue.atMs, readyAtMs);
  const fade = Math.min(240, (cue.untilMs - start) / 2);
  if (fade <= 0) return 0;
  const progress = Math.max(0, Math.min(1, (elapsedMs - start) / fade, (cue.untilMs - elapsedMs) / fade));
  return progress * progress * (3 - 2 * progress);
}
